import { forwardRef, ButtonHTMLAttributes, MouseEvent } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'gradient'
  | 'ghost'
  | 'link';
export type ButtonSize = 'default' | 'sm' | 'lg';
export type ButtonShape = 'square' | 'circle';

// Radius is a two-value system: every control is either a rounded rectangle or a
// pill/circle. No third radius.
const shapes: Record<ButtonShape, string> = {
  square: 'rounded-lg',
  circle: 'rounded-full',
};

const baseClasses =
  "inline-flex items-center justify-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--bg-primary))] disabled:pointer-events-none disabled:opacity-50";

const variants: Record<ButtonVariant, string> = {
  // Opaque accent fill: --accent-fill (background role) + --accent-fg (white) +
  // a 1px --accent-primary border, which is what carries WCAG 1.4.11 boundary
  // contrast — the deep fill alone is 1.72–2.06 against the dark slate surfaces.
  // Hover/active are derived from --fill-state-tint, not minted as tokens.
  default: "bg-accent-fill text-accent-fg border border-accent-primary hover:bg-[color:color-mix(in_oklab,rgb(var(--accent-fill)),rgb(var(--fill-state-tint))_8%)] active:bg-[color:color-mix(in_oklab,rgb(var(--accent-fill)),rgb(var(--fill-state-tint))_14%)]",
  // The gradient variant is NOT a fill site — its stops are --accent-primary /
  // --accent-secondary, which are 400-level in dark mode. White on those is
  // 2.26–2.72:1, so it takes --accent-fg-tint (the on-tint foreground) rather
  // than --accent-fg (the on-fill foreground). --accent-fg-tint is mode-scoped
  // in globals.css and needs no dark: override here.
  gradient: "bg-gradient-to-r from-[rgb(var(--accent-primary))] via-[rgb(var(--accent-secondary))] to-[rgb(var(--accent-primary))] bg-300% animate-gradient-x hover:scale-105 text-accent-fg-tint font-medium shadow-lg",
  secondary: "bg-[rgb(var(--bg-tertiary))] text-[rgb(var(--text-primary))] hover:opacity-80 border border-[rgb(var(--border-color))]",
  destructive: "bg-error text-error-fg hover:bg-error/90 shadow-sm",
  ghost: "bg-transparent hover:bg-[rgb(var(--bg-tertiary))] text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--text-primary))]",
  link: 'text-primary hover:underline bg-transparent h-auto p-0',
};

// Heights are bound to the ctrl-* tokens in tailwind.config.ts rather than to raw
// numbers, so the control-height floor is defined in exactly one place.
// Horizontal padding is unchanged from the pre-token sizes.
const sizes: Record<ButtonSize, string> = {
  default: 'h-ctrl px-s4 py-s2',
  sm: 'h-ctrl-sm px-s3',
  lg: 'h-ctrl-lg px-s8',
};

// Icon-only controls take the same three heights and derive their width from
// `aspect-square`, so width can never drift from the height token. `shrink-0`
// stops a flex parent from squashing the square back into a rectangle.
const iconSizes: Record<ButtonSize, string> = {
  default: 'h-ctrl',
  sm: 'h-ctrl-sm',
  lg: 'h-ctrl-lg',
};

// ctrl-sm (32px) and ctrl (36px) both render below the 44px pointer-target floor.
// An absolutely-positioned pseudo-element extends the hit area to 44px without
// participating in layout, so the control still occupies its token height.
// Centred with inset-0 + m-auto rather than a translate, which is direction
// agnostic and therefore correct under RTL. ctrl-lg is already 44px.
const hitArea =
  "relative after:absolute after:inset-0 after:m-auto after:h-[2.75rem] after:w-[2.75rem] after:content-['']";

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  shape?: ButtonShape;
  className?: string;
}

/**
 * The button style system, decoupled from the component.
 *
 * Exported so sibling controls (a future Toggle, for example) can adopt the exact
 * same visual treatment without importing `Button` and inheriting its semantics —
 * notably `aria-pressed`, which is deliberately NOT a Button concern.
 */
export function buttonClasses({
  variant = 'default',
  size = 'default',
  iconOnly = false,
  shape = 'square',
  className,
}: ButtonClassOptions = {}): string {
  return cn(
    baseClasses,
    shapes[shape],
    variants[variant],
    iconOnly ? iconSizes[size] : sizes[size],
    iconOnly && 'aspect-square shrink-0 p-0',
    iconOnly && size !== 'lg' && hitArea,
    className
  );
}

type ButtonOwnProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  loading?: boolean;
};

type ButtonBaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> &
  ButtonOwnProps;

/**
 * `aria-label` is required BY TYPE on the icon-only branch: a control with no text
 * content has no accessible name, so omitting the label is a compile error rather
 * than something a lint rule or a runtime warning has to catch after the fact.
 */
type IconOnlyButtonProps = ButtonBaseProps & {
  iconOnly: true;
  'aria-label': string;
};

type TextButtonProps = ButtonBaseProps & {
  iconOnly?: false;
  'aria-label'?: string;
};

export type ButtonProps = IconOnlyButtonProps | TextButtonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'default',
    size = 'default',
    shape = 'square',
    iconOnly = false,
    loading = false,
    disabled = false,
    // Raw <button> defaults to type="submit", which silently submits any
    // enclosing form. The shared component does not inherit that ambiguity;
    // callers that want a submit button say so explicitly.
    type = 'button',
    onClick,
    children,
    ...props
  },
  ref
) {
  // While loading the control is busy, not unavailable: a hard `disabled`
  // attribute would drop focus mid-interaction and remove the button from the
  // a11y tree. `aria-disabled` + click suppression keeps focus where it is.
  // A caller-supplied `disabled` still maps to the real attribute, because that
  // means the action is genuinely unavailable.
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      className={cn(
        buttonClasses({ variant, size, iconOnly, shape, className }),
        // Positioning context for the loading overlay. Applied only while
        // loading so a non-loading button does not become an anchor for any
        // absolutely-positioned content a caller nests inside it.
        loading && 'relative'
      )}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...props}
    >
      {loading && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          {/* The spinner is overlaid rather than inserted inline so swapping the
              label for the busy state does not change the control's width and
              reflow the row around it. It is centred by the flex container, so
              it carries no inline margin; any spacing added here must use
              logical properties (ms-/me-/ps-/pe-) — the app renders Hebrew. */}
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </span>
      )}
      {loading ? (
        <span className="invisible inline-flex items-center">{children}</span>
      ) : (
        children
      )}
    </button>
  );
});
Button.displayName = "Button";

export { Button };
